import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, MessageCircle, ChevronRight } from "lucide-react";
import VerifiedBadge from "@/components/plyx/VerifiedBadge";
import { toast } from "sonner";

/**
 * Plyx DM Inbox — /plyx/dm
 * Lists conversations sorted by last_message_at desc with unread badges.
 * Polls /unread-count + /conversations every 15s so a fresh message lands
 * in the inbox without a hard reload.
 */
const fmtTimeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
};

const PlyxDMInbox = () => {
  const { user } = usePlyxAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchInbox = () => {
      plyxApi.get("/plyx/dm/conversations?limit=50")
        .then((r) => { if (alive) setItems(r.data?.items || []); })
        .catch(() => { /* silent retry */ })
        .finally(() => { if (alive) setLoading(false); });
    };
    fetchInbox();
    const t = setInterval(fetchInbox, 15_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-5">
        <div className="text-center">
          <MessageCircle className="w-12 h-12 mx-auto text-white/30 mb-3" />
          <div className="font-semibold">Sign in to message creators.</div>
          <Link to="/plyx/login" className="mt-3 inline-block px-5 py-2 rounded-full bg-white text-white text-sm font-bold">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="plyx-dm-inbox">
      <div className="max-w-2xl mx-auto px-5 py-6">
        <h1 className="font-display text-3xl tracking-tight mb-5 flex items-center gap-2">
          <MessageCircle className="w-7 h-7 text-[#FFB42D]" /> Messages
        </h1>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-white/30" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-12 text-center" data-testid="plyx-dm-inbox-empty">
            <MessageCircle className="w-12 h-12 mx-auto text-white/30 mb-3" />
            <div className="font-semibold">No conversations yet.</div>
            <div className="text-xs text-white/55 mt-1">
              Tap "Message" on any creator profile to start chatting.
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {items.map((c) => (
              <Link
                key={c.conversation_id}
                to={`/plyx/dm/${c.conversation_id}`}
                data-testid={`plyx-dm-row-${c.conversation_id}`}
                className="flex items-center gap-3 p-3 rounded-2xl hover:bg-white/5 transition-colors"
              >
                <div className="relative w-12 h-12 rounded-full bg-white/10 overflow-hidden flex-shrink-0">
                  {c.partner.avatar_url ? (
                    <img src={plyxMediaUrl(c.partner.avatar_url)} alt={c.partner.handle} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/60 font-semibold">
                      {(c.partner.display_name || c.partner.handle || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm truncate inline-flex items-center gap-1">
                      @{c.partner.handle}
                      <VerifiedBadge user={c.partner} size={12} />
                    </div>
                    <div className="text-[11px] text-white/45 shrink-0">{fmtTimeAgo(c.last_message_at)}</div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className={`text-xs truncate ${c.unread_count > 0 ? "text-white font-medium" : "text-white/55"}`}>
                      {c.is_last_from_me ? "You: " : ""}{c.last_message || "Say hi 👋"}
                    </div>
                    {c.unread_count > 0 && (
                      <div className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] text-white" data-testid={`plyx-dm-unread-${c.conversation_id}`}>
                        {c.unread_count}
                      </div>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyxDMInbox;
